import { trackProps } from '../../helpers/utils/analytics';
import { useState, useRef, useEffect } from 'react';
import { Pencil } from 'lucide-react';
import { Center } from '../ui/center';
import { Text } from '../ui';
import { useNameLimit } from '../../helpers/utils/useNameLimit';
import { NAME_LIMIT_MESSAGE } from '../../helpers/utils/constants';
import { HINT_PAD_Y } from './figmaLayout';

interface TeamNameInputProps {
  id: string;
  teamName: string;
  handleChange: (
    e:
      | { target: { id: string; value: string } }
      | React.ChangeEvent<HTMLInputElement>,
  ) => void;
  teamClassName: string;
}

// Emerald/Team names (Figma 1796:20872). One half of the row: the name is
// centred in its half, capped at --spades-home-team-name-max (166 / 158 / 150
// by screen width) with the pencil kept clear, and past that it ends in an
// ellipsis. Tapping it swaps in a 32pt field with a team-coloured outline.
const NAME_TEXT = {
  fontSize: 'var(--spades-font-size-sm)',
  fontWeight: 700,
  lineHeight: 1.25,
  letterSpacing: 'var(--spades-tracking-md)',
  textTransform: 'uppercase',
  color: 'var(--spades-team-base)',
} as const;

function TeamNameInput({
  id,
  teamName,
  handleChange,
  teamClassName,
}: TeamNameInputProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(teamName);
  const inputRef = useRef<HTMLInputElement>(null);
  const { limit, showHint } = useNameLimit();

  // Sync internal state with external prop
  useEffect(() => {
    setValue(teamName);
  }, [teamName]);

  // Focus and select text when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const onCommit = () => {
    setIsEditing(false);
    if (value !== teamName) {
      handleChange({ target: { id, value } });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onCommit();
    }
  };

  return (
    <Center
      width="100%"
      height="32px"
      position="relative"
      data-team={teamClassName === 'team2' ? '2' : '1'}
    >
      {isEditing ? (
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(limit(e.target.value))}
          onBlur={onCommit}
          onKeyDown={handleKeyDown}
          className={teamClassName}
          id={id}
          data-cy={id + 'Input'}
          style={{
            ...NAME_TEXT,
            boxSizing: 'border-box',
            width: 'var(--spades-home-team-name-max)',
            height: '32px',
            padding: '0 10px',
            textAlign: 'center',
            background: 'var(--spades-background)',
            border: '2px solid var(--spades-team-base)',
            borderRadius: '10px',
            boxShadow:
              '0 0 12px color-mix(in srgb, var(--spades-team-base) 50%, transparent)',
            caretColor: 'var(--spades-team-base)',
            outline: 'none',
          }}
        />
      ) : (
        <div
          {...trackProps('team_name_edit_opened')}
          onClick={() => setIsEditing(true)}
          className={teamClassName}
          id={id}
          data-cy={id + 'Input'}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            height: '32px',
            maxWidth: 'min(var(--spades-home-team-name-max), 100%)',
            cursor: 'pointer',
          }}
        >
          <span
            style={{
              ...NAME_TEXT,
              flex: '0 1 auto',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value || 'Enter Team Name'}
          </span>
          <Pencil
            aria-hidden="true"
            size={11.7}
            strokeWidth={2}
            color="var(--spades-team-base)"
            style={{ flexShrink: 0 }}
          />
        </div>
      )}
      {/* Floats just above the name, in the gap under the title, so it never
          covers the table below. */}
      {isEditing && showHint && (
        <Text
          role="status"
          color="spades.error"
          fontSize="var(--spades-font-size-2xs)"
          fontWeight="700"
          position="absolute"
          bottom="100%"
          left="50%"
          transform="translateX(-50%)"
          zIndex={1}
          bg="var(--spades-background)"
          borderRadius="var(--spades-radius-pill)"
          px="var(--spades-spacing-sm)"
          py={HINT_PAD_Y}
          lineHeight={1.25}
          whiteSpace="nowrap"
        >
          {NAME_LIMIT_MESSAGE}
        </Text>
      )}
    </Center>
  );
}

export default TeamNameInput;
